import React, {useState, useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './notifications.scss';
import {Loader, Pagination} from 'components';
import {api} from 'api/calls';
import {generalExtraActions} from 'reduxStore/actions';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import {FaRegEye, FaRegCheckCircle, FaRegCircle, FaCheck} from 'react-icons/fa';
import {MdNotifications, MdNotificationsActive} from 'react-icons/md';
import {format} from 'date-fns';

const readStatusFilterOptions = [
  {label: 'Unread', value: 'unread'},
  {label: 'Read', value: 'read'},
];

const typeFilterOptions = [
  {label: 'Booking', value: 'booking'},
  {label: 'Review', value: 'review'},
];

const sortOptions = [
  {value: '-created', label: 'Date (Newest)'},
  {value: 'created', label: 'Date (Oldest)'},
  {value: 'ground_admin_read', label: 'Read Status'},
  {value: '-ground_admin_read', label: 'Unread First'},
];

const getNotificationIcon = (type) => {
  if (type.includes('booking')) {
    return <FaRegCheckCircle className="notification-icon booking-icon"/>;
  } else if (type.includes('review')) {
    return <MdNotifications className="notification-icon review-icon"/>;
  }
  return <MdNotificationsActive className="notification-icon default-icon"/>;
};

const getNotificationTypeLabel = (type) => {
  const typeMap = {
    'booking_created': 'New Booking',
    'booking_confirmed': 'Booking Confirmed',
    'booking_cancelled': 'Booking Cancelled',
    'booking_completed': 'Booking Completed',
    'booking_declined': 'Booking Declined',
    'booking_reminder': 'Booking Reminder',
    'review_created': 'New Review',
    'review_updated': 'Review Updated',
  };
  return typeMap[type] || type;
};

const getNotificationTypeClass = (type) => {
  if (type.includes('booking')) return 'booking-notification';
  if (type.includes('review')) return 'review-notification';
  return 'default-notification';
};

function Notifications() {
  const dispatch = useDispatch();
  const {notifications, notificationsLoading, notificationsError, notificationsPagination} = useSelector((state) => state.general);

  const [readStatusFilter, setReadStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('-created');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [readStatusFilter, typeFilter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };

    if (sortBy) {
      params.ordering = sortBy;
    }

    if (readStatusFilter !== 'all') {
      if (readStatusFilter === 'unread') {
        params.ground_admin_read = 'false';
      } else if (readStatusFilter === 'read') {
        params.ground_admin_read = 'true';
      }
    }

    if (typeFilter !== 'all') {
      if (typeFilter === 'booking') {
        params.type__startswith = 'booking';
      } else if (typeFilter === 'review') {
        params.type__startswith = 'review';
      }
    }

    if (search) {
      params.search = search;
    }

    return params;
  }, [currentPage, itemsPerPage, sortBy, readStatusFilter, typeFilter, search]);

  // Fetch notifications
  const fetchNotifications = useCallback(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.fetchNotifications(params));
  }, [dispatch, buildApiParams]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleReadStatusFilter = (status) => {
    setReadStatusFilter(prev => (prev === status ? 'all' : status));
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(prev => (prev === type ? 'all' : type));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleNotificationSelect = (notificationId) => {
    setSelectedNotifications(prev =>
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    const unreadNotifications = notifications.filter(n => !n.ground_admin_read);
    const unreadIds = unreadNotifications.map(n => n.id);

    if (selectedNotifications.length === unreadIds.length && unreadIds.length > 0) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(unreadIds);
    }
  };

  const handleMarkAsRead = async () => {
    if (selectedNotifications.length === 0) return;

    try {
      await api.markNotificationsRead({ notification_ids: selectedNotifications });
      setSelectedNotifications([]);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.markAllNotificationsRead();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleViewDetail = async (notification) => {
    setSelectedNotification(notification);
    setShowDetailModal(true);

    // Mark as read if not already read
    if (!notification.ground_admin_read) {
      try {
        await api.markNotificationsRead({ notification_ids: [notification.id] });
        setSelectedNotifications([]);
        fetchNotifications();
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedNotification(null);
  };

  const totalItems = notificationsPagination?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (notificationsError) {
    return (
      <div className="notifications-page custom-listing">
        <div className="error-message">
          <h3>Error loading notifications</h3>
          <p>{notificationsError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.fetchNotifications(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page custom-listing">
      {notificationsLoading && <Loader/>}
      <div className="listing-header">
        <div className="listing-title">Notifications Center</div>
        <button className="listing-create-btn" onClick={handleMarkAllAsRead} disabled={notifications.length === 0}>
          <FaCheck /> Mark All as Read
        </button>
      </div>
      <div className="listing-filters">
        <div>
          <Filters filters={readStatusFilterOptions} selectedFilter={readStatusFilter} handleFilter={handleReadStatusFilter}/>
          {/*<Filters filters={typeFilterOptions} selectedFilter={typeFilter} handleFilter={handleTypeFilter}/>*/}
        </div>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange}/>
          <SearchBar value={search} onChange={handleSearch} placeholder="Search notifications..." debounceDelay={500}
                     onDebouncedChange={handleSearch}/>
        </div>
      </div>

      {selectedNotifications.length > 0 && (
        <div className="bulk-actions">
          <span className="selected-count">
            {selectedNotifications.length} unread notification(s) selected
          </span>
          <button
            className="btn btn-primary"
            onClick={handleMarkAsRead}
          >
            <FaRegCheckCircle /> Mark as Read
          </button>
        </div>
      )}

      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>
              {notifications.some(n => !n.ground_admin_read) && (
                <input
                  type="checkbox"
                  checked={selectedNotifications.length === notifications.filter(n => !n.ground_admin_read).length && notifications.filter(n => !n.ground_admin_read).length > 0}
                  onChange={handleSelectAll}
                />
              )}
            </th>
            <th>Title</th>
            <th>Message</th>
            <th>Status</th>
            <th>Date</th>
            <th>View</th>
          </tr>
          </thead>
          <tbody>
          {notifications.length === 0 ? (
            <tr>
              <td colSpan="7" style={{textAlign: 'center', color: '#888'}}>No notifications found.</td>
            </tr>
          ) : (
            notifications.map((notification) => (
              <tr key={notification.id} className={!notification.ground_admin_read ? 'unread-row' : ''}>
                <td>
                  {!notification.ground_admin_read && (
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.id)}
                      onChange={() => handleNotificationSelect(notification.id)}
                    />
                  )}
                </td>
                <td>
                  <div className="notification-title-cell">
                    {getNotificationIcon(notification.type)}
                    {notification.title}
                    {!notification.ground_admin_read && <span className="unread-indicator"></span>}
                  </div>
                </td>
                <td>
                  <div className="notification-message-cell">
                    {notification.message.length > 100
                      ? `${notification.message.substring(0, 100)}...`
                      : notification.message
                    }
                  </div>
                </td>
                <td>
                  <span className={`badge badge-status${notification.ground_admin_read ? '' : ' inactive'}`}>
                    {notification.ground_admin_read ? 'Read' : 'Unread'}
                  </span>
                </td>
                <td>{format(new Date(notification.created), 'MMM dd, yyyy HH:mm')}</td>
                <td>
                  <button
                    className="view-btn"
                    onClick={() => handleViewDetail(notification)}
                    title="View notification details"
                  >
                    <FaRegEye/>
                  </button>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {showDetailModal && selectedNotification && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Notification Details</h3>
              <button className="modal-close" onClick={closeDetailModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="notification-detail">
                <div className="detail-header">
                  <div className="detail-icon">
                    {getNotificationIcon(selectedNotification.type)}
                  </div>
                  <div className="detail-info">
                    <h4>{selectedNotification.title}</h4>
                    <span className="detail-type">
                      {getNotificationTypeLabel(selectedNotification.type)}
                    </span>
                    <span className="detail-date">
                      {format(new Date(selectedNotification.created), 'MMM dd, yyyy HH:mm')}
                    </span>
                    <div className="detail-status">
                      <span className={`badge badge-status${selectedNotification.ground_admin_read ? '' : ' inactive'}`}>
                        {selectedNotification.ground_admin_read ? 'Read' : 'Unread'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="detail-message">
                  <p>{selectedNotification.message}</p>
                </div>

                {selectedNotification.booking_id && (
                  <div className="detail-booking">
                    <h5>Booking Information</h5>
                    <p><strong>Ground:</strong> {selectedNotification.ground_name}</p>
                    <p><strong>Date:</strong> {format(new Date(selectedNotification.booking_date), 'MMM dd, yyyy')}</p>
                    <p><strong>Slot:</strong> {selectedNotification.booking_slot}</p>
                  </div>
                )}

                {selectedNotification.review_id && (
                  <div className="detail-review">
                    <h5>Review Information</h5>
                    <p><strong>Rating:</strong> {selectedNotification.review_rating} stars</p>
                    <p><strong>Ground:</strong> {selectedNotification.ground_name}</p>
                    {selectedNotification.review_comment && (
                      <p><strong>Comment:</strong> {selectedNotification.review_comment}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;